import React, { useState, useEffect } from 'react';
import { Box, HStack, VStack, Image, Text, Divider } from '@chakra-ui/react';
import useLogoUrl from '../hooks/useLogoUrl';
import JIMIWellnessLogo from './JIMIWellnessLogo';

interface JIMILogoProps {
  size?: 'sm' | 'md' | 'lg';
  showDivider?: boolean;
  showTagline?: boolean;
  logoHeight?: string;
  containerProps?: any;
  height?: string;
  mr?: number;
}

/**
 * A reusable JIMI Wellness Platform logo component
 * Can be used throughout the presentation with consistent styling
 */
const JIMILogo: React.FC<JIMILogoProps> = ({
  size = 'md',
  showDivider = true,
  showTagline = true,
  logoHeight,
  containerProps = {},
  height,
  mr
}) => {
  // Use the useLogoUrl hook to get the logo URL and refresh function
  const { logoUrl, refreshLogo, loading, error } = useLogoUrl();
  
  // Get cache buster from localStorage or use a default value
  const [cacheBuster, setCacheBuster] = useState<string>(() => {
    const storedCacheBuster = localStorage.getItem('logoCacheBuster');
    return storedCacheBuster || '20250301130250';
  });

  // Listen for changes to the cache buster in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'logoCacheBuster' && e.newValue) {
        setCacheBuster(e.newValue);
        refreshLogo(); // Call refreshLogo when cache buster changes
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Define sizes for different variants
  const sizes = {
    sm: {
      logoHeight: logoHeight || '35px',
      titleSize: 'sm',
      titleLineHeight: '0.9',
      taglineSize: 'xs',
      dividerHeight: '35px',
      spacing: 3
    },
    md: {
      logoHeight: logoHeight || '50px',
      titleSize: 'md',
      titleLineHeight: '1',
      taglineSize: 'sm',
      dividerHeight: '50px',
      spacing: 4
    },
    lg: {
      logoHeight: logoHeight || '60px',
      titleSize: 'lg',
      titleLineHeight: '1.1',
      taglineSize: 'md',
      dividerHeight: '60px',
      spacing: 5
    }
  };

  // Ensure we have a valid size, defaulting to 'md' if the provided size is invalid
  const currentSize = sizes[size] || sizes['md'];
  
  // Use the actual logoUrl from the hook with the cache buster
  const finalLogoUrl = `${logoUrl.split('?')[0]}?v=${cacheBuster}`;

  return (
    <HStack 
      spacing={currentSize.spacing} 
      justify="center" 
      align="center"
      bg="rgba(255, 255, 255, 0.9)"
      p={3}
      borderRadius="lg"
      boxShadow="sm"
      border="1px solid"
      borderColor="rgba(220, 220, 255, 0.5)"
      transition="all 0.5s ease"
      _hover={{ boxShadow: "md" }}
      height={height}
      mr={mr}
      {...containerProps}
    >
      <JIMIWellnessLogo 
        size={size} 
        showTagline={showTagline} 
      />
      
      {showDivider && (
        <Divider orientation="vertical" height={currentSize.dividerHeight} borderColor="gray.300" />
      )}
    </HStack>
  );
};

export default JIMILogo;
